import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';

/**
 * Base composable for article feedback customisation
 * Returns common feedback fields (label text, label color, action color)
 * Can be extended by specific feedback composables for additional fields
 *
 * @param sectionKey - The section key for the specific feedback (POSITIVE_FEEDBACK or NEGATIVE_FEEDBACK)
 * @returns Object containing common feedback fields and getField function for extensions
 */
const useArticleFeedbackCustomisationBase = (sectionKey: string) => {
  const { getField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE,
    sectionKey,
    blockKey: PUI.ARTICLES_BLOCK_KEYS.BODY_SECTION,
  });

  const labelText = getField(PUI.COMMON_FIELD_KEYS.LABEL_TEXT);
  const labelColor = getField(PUI.COMMON_FIELD_KEYS.LABEL_COLOR);
  const actionColor = getField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR);

  return {
    isVisible,
    labelText,
    labelColor,
    actionColor,
    getField, // Expose getField for extensions
  };
};

/**
 * Composable for Positive Feedback customisation
 * Returns positive feedback button/text customization
 * Can be extended with positive-feedback-specific fields if needed in the future
 *
 * @example
 * // To add positive-feedback-specific fields in the future:
 * export const usePositiveFeedbackCustomisation = () => {
 *   const base = useArticleFeedbackCustomisationBase(PUI.ARTICLES_SECTION_KEYS.POSITIVE_FEEDBACK);
 *   const positiveIcon = base.getField('positiveIcon');
 *   const { getField, ...rest } = base;
 *   return { ...rest, positiveIcon };
 * };
 */
export const usePositiveFeedbackCustomisation = () => {
  const base = useArticleFeedbackCustomisationBase(PUI.ARTICLES_SECTION_KEYS.POSITIVE_FEEDBACK);
  const { getField, ...rest } = base;
  return rest;
};

/**
 * Composable for Negative Feedback customisation
 * Returns negative feedback button/text customization
 * Can be extended with negative-feedback-specific fields if needed in the future
 *
 * @example
 * // To add negative-feedback-specific fields in the future:
 * export const useNegativeFeedbackCustomisation = () => {
 *   const base = useArticleFeedbackCustomisationBase(PUI.ARTICLES_SECTION_KEYS.NEGATIVE_FEEDBACK);
 *   const negativeIcon = base.getField('negativeIcon');
 *   const { getField, ...rest } = base;
 *   return { ...rest, negativeIcon };
 * };
 */
export const useNegativeFeedbackCustomisation = () => {
  const base = useArticleFeedbackCustomisationBase(PUI.ARTICLES_SECTION_KEYS.NEGATIVE_FEEDBACK);
  const { getField, ...rest } = base;
  return rest;
};
